import { z } from 'zod';

/**
 * The dashboard's time scope.
 *
 * The last-used value is persisted per user on the *client*
 * (client/lib/preferences.ts), not on the server. It is a display preference,
 * and a `User.preferences` column would be the wrong place to start keeping UI
 * state.
 */
const timeScopeSchema = z.enum(['today', 'week', 'month']);
export type TimeScope = z.infer<typeof timeScopeSchema>;

export const TIME_SCOPE_LABELS = {
  today: 'Today',
  week: 'This week',
  month: 'This month',
} as const satisfies Record<TimeScope, string>;

export const dashboardQuerySchema = z.object({
  scope: timeScopeSchema.default('today'),
});
export type DashboardQuery = z.infer<typeof dashboardQuerySchema>;

/**
 * How the top-tests ranking is ordered.
 *
 * `amount` is **billed value at list price**, not money taken. Payment is
 * recorded per visit and never per report, so there is nothing to sum per test
 * that represents cash — see `modeCaption`, which says so on the card.
 */
export const topTestsModeSchema = z.enum(['count', 'amount']);
export type TopTestsMode = z.infer<typeof topTestsModeSchema>;

export const TOP_TESTS_MODE_LABELS = {
  count: 'Times performed',
  amount: 'Value billed',
} as const satisfies Record<TopTestsMode, string>;

/**
 * Its own endpoint, not a parameter on `GET /dashboard`.
 *
 * The mode is a user-facing toggle. Folding it into the snapshot would mean
 * flipping it refetches thirteen queries in a transaction, plus the queue, the
 * attention feed and two raw series — to reorder five rows.
 */
export const topTestsQuerySchema = z.object({
  scope: timeScopeSchema.default('today'),
  by: topTestsModeSchema.default('count'),
  limit: z.coerce.number().int().min(1).max(20).default(5),
  /**
   * Count only visits settled in full.
   *
   * The honest half of "which tests earn most": on a visit marked `paid`, the
   * line items genuinely were paid for. It invents nothing, unlike apportioning
   * a partial payment across a visit's reports.
   */
  paid_only: z.enum(['true', 'false']).default('false'),
});
export type TopTestsQuery = z.infer<typeof topTestsQuerySchema>;

/**
 * A tile's tone follows the convention the research settled on: green when a
 * figure is on target, amber when it needs attention today, red when it is past
 * a threshold. Neutral is for figures that are information rather than a
 * target — how much was billed is not "good" or "bad".
 */
export type KpiTone = 'positive' | 'attention' | 'critical' | 'neutral';

type KpiKey =
  | 'awaiting_results'
  | 'in_progress'
  | 'overdue'
  | 'completed_in_scope'
  | 'revenue_in_scope'
  | 'outstanding_balance';

type AttentionKind = 'overdue_report' | 'stalled_registration' | 'unpaid_completed';

export interface KpiTile {
  key: KpiKey;
  label: string;
  value: number;
  /** Render as currency rather than a plain integer. */
  is_currency: boolean;
  tone: KpiTone;
  /**
   * Shown *instead of* the numeral when the value is zero.
   *
   * A dashboard should never present a bare "0". "0" next to "Past due" reads
   * as a missing figure or a broken query; "Nothing overdue" reads as the good
   * news it actually is.
   */
  zero_label: string;
  /** Where the tile drills through to, pre-filtered. */
  href: string;
  /** Seven daily buckets ending today, oldest first. Empty when not applicable. */
  spark: number[];
}

export interface AttentionItem {
  kind: AttentionKind;
  /** Stable key for React, and for de-duplicating across kinds. */
  id: string;
  title: string;
  detail: string;
  href: string;
  /** ISO timestamp of whatever made this item interesting. */
  at: string;
  tone: KpiTone;
}

export interface DailyPoint {
  /**
   * The day, as `YYYY-MM-DD` in the lab's local timezone.
   *
   * Deliberately a plain date rather than an ISO instant: local midnight
   * serialised with `toISOString()` renders as the *previous* day in any
   * timezone ahead of UTC, so an axis label built from it would be off by one
   * for exactly the users this app is for.
   */
  bucket: string;
  count: number;
}

export interface TopTest {
  test_id: number;
  name: string;
  count: number;
  /** Null for roles without billing access — the server omits it, not the UI. */
  revenue: number | null;
}

export interface TopTestRow extends TopTest {
  code: string;
  /**
   * This row's value against the leader's, 0 to 1.
   *
   * Against the leader rather than the total: the bars compare five tests with
   * each other, and a flat distribution measured against a total renders five
   * stubs that say nothing.
   */
  share: number;
}

export interface TopTestsResult {
  scope: TimeScope;
  range: { from: string; to: string };
  /**
   * The mode that was actually applied.
   *
   * Differs from what was asked for when the caller cannot see billing: the
   * server coerces to `count` rather than sorting by revenue and nulling the
   * column, because the *ordering* is the fact being withheld.
   */
  by: TopTestsMode;
  requested_by: TopTestsMode;
  paid_only: boolean;
  /** What the card prints under its heading. Never invented on the client. */
  caption: string;
  rows: TopTestRow[];
}
